import { useCallback, useMemo } from "react";

import { useBlock } from "@/components/PowerBlock/Block/block-store";
import { useLLMProviderStore } from "@/providers/ProviderAndModelsProvider/provider-store";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useParseInputVariables, usePrompt, Entity, EntityType } from "@/queries";
import { PromptBlueprint } from "@/types";
import { PromptTemplateConfiguration } from "@/types/evaluate";
import { useVerifyReleaseLabelExistence } from "@/queries/prompts/useVerifyReleaseLabelExistence";
import { getStringContent } from "@/utils/utils";

export const usePromptTemplate = () => {
  const blockStore = useBlock<PromptTemplateConfiguration>();
  const auth = useAuth();
  const userToken = auth?.userToken || "";
  const workspaceId = useUser()?.activeWorkspaceId || 0;
  const { store: providerStore } = useLLMProviderStore();

  const releaseLabelExists = useVerifyReleaseLabelExistence({
    releaseLabelName: blockStore.configuration?.template?.label || "",
    promptName: blockStore.configuration?.template?.name || "",
    enabled: blockStore.configuration?.template?.label !== undefined,
  });

  const promptTemplateVersionQuery = usePrompt(userToken, {
    workspace_id: workspaceId,
    prompt_name: blockStore.configuration?.template?.name,
    number: blockStore.configuration?.template?.version_number ?? undefined,
    label: releaseLabelExists.data?.exists ? blockStore.configuration?.template?.label : undefined,
  });

  const promptId = promptTemplateVersionQuery?.data?.prompt_id;
  const selectedPromptTemplateVersion = promptTemplateVersionQuery.data;

  // Determine if we're in inline mode
  const isInlineMode = Boolean(blockStore.configuration?.inline_template?.prompt_template);

  // For inline mode, use the inline template; for registry mode, use the selected version
  const templateForParsing = useMemo(() => {
    if (isInlineMode && blockStore.configuration?.inline_template?.prompt_template) {
      return {
        prompt_template: blockStore.configuration.inline_template.prompt_template,
        metadata: blockStore.configuration.inline_template.metadata,
      } as PromptBlueprint;
    }
    return selectedPromptTemplateVersion as PromptBlueprint;
  }, [isInlineMode, blockStore.configuration?.inline_template, selectedPromptTemplateVersion]);

  const parseInputVariables = useParseInputVariables(userToken, templateForParsing);

  const inputVariables = parseInputVariables.data?.success
    ? parseInputVariables.data?.input_variables
    : [];

  // Extract template format from the selected prompt template or inline template
  const templateFormat = useMemo(() => {
    // For inline mode, use inline template
    if (isInlineMode && blockStore.configuration?.inline_template?.prompt_template) {
      const template = blockStore.configuration.inline_template.prompt_template;

      // For completion templates
      if (template.type === "completion" && "template_format" in template) {
        return template.template_format;
      }

      // For chat templates, get format from first message
      if (template.type === "chat" && template.messages?.length > 0) {
        return template.messages[0].template_format;
      }

      return undefined;
    }

    // For registry mode, use selected prompt template version
    if (!selectedPromptTemplateVersion?.prompt_template) {
      return undefined;
    }

    const template = selectedPromptTemplateVersion.prompt_template;

    // For completion templates
    if (template.type === "completion" && "template_format" in template) {
      return template.template_format;
    }

    // For chat templates, get format from first message
    if (template.type === "chat" && template.messages?.length > 0) {
      return template.messages[0].template_format;
    }

    return undefined;
  }, [isInlineMode, blockStore.configuration?.inline_template, selectedPromptTemplateVersion]);

  const handleTemplateSelection = useCallback(
    (entity: Entity<EntityType.PROMPT | EntityType.SNIPPET>) => {
      try {
        if (
          !entity.id ||
          !entity.name ||
          typeof entity.id !== "number" ||
          typeof entity.name !== "string"
        ) {
          // eslint-disable-next-line no-console
          console.warn("Invalid template selection data:", entity);
          return;
        }

        blockStore.setConfiguration({
          ...blockStore.configuration,
          template: {
            id: entity.id,
            name: entity.name,
            version_number: entity.version_number ?? null,
            label: entity.label,
          },
          prompt_template_variable_mappings:
            blockStore.configuration?.prompt_template_variable_mappings || {},
        });

        // Only update name if it's still the default or empty
        if (blockStore.isDefaultName || !blockStore.name || blockStore.name.trim() === "") {
          // Check if no other blocks/allSources have the same name as template
          if (blockStore.allSources) {
            const allExistingNames: string[] = [];
            Object.values(blockStore.allSources).forEach(sourceArray => {
              allExistingNames.push(...sourceArray);
            });

            const nameExists = allExistingNames.includes(entity.name);
            if (!nameExists) {
              blockStore.setName(entity.name);
              // Keep isDefaultName as true since this is automatic
            }
          } else if (!blockStore.name || blockStore.name.trim() === "") {
            // If no allSources available and block name is empty, use template name
            blockStore.setName(entity.name);
            // Keep isDefaultName as true since this is automatic
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error handling template selection:", error);
      }
    },
    [blockStore],
  );

  const handleVerboseChange = useCallback(
    (enabled: boolean) => {
      try {
        if (typeof enabled !== "boolean") {
          // eslint-disable-next-line no-console
          console.warn("Invalid verbose mode value:", enabled);
          return;
        }

        blockStore.setConfiguration({
          ...blockStore.configuration,
          verbose: enabled,
        } as PromptTemplateConfiguration);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error updating verbose mode:", error);
      }
    },
    [blockStore],
  );

  const handleReturnTemplateOnlyChange = useCallback(
    (enabled: boolean) => {
      try {
        if (typeof enabled !== "boolean") {
          // eslint-disable-next-line no-console
          console.warn("Invalid return_template_only value:", enabled);
          return;
        }

        blockStore.setConfiguration({
          ...blockStore.configuration,
          return_template_only: enabled,
          // When enabling return_template_only, default to applying input variables
          hydrate_input_variables: enabled
            ? true
            : blockStore.configuration?.hydrate_input_variables,
        } as PromptTemplateConfiguration);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error updating return_template_only:", error);
      }
    },
    [blockStore],
  );

  const handleHydrateInputVariablesChange = useCallback(
    (enabled: boolean) => {
      try {
        if (typeof enabled !== "boolean") {
          // eslint-disable-next-line no-console
          console.warn("Invalid hydrate_input_variables value:", enabled);
          return;
        }

        blockStore.setConfiguration({
          ...blockStore.configuration,
          hydrate_input_variables: enabled,
        } as PromptTemplateConfiguration);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error updating hydrate_input_variables:", error);
      }
    },
    [blockStore],
  );

  const handleChatHistoryToggle = useCallback(
    (enabled: boolean) => {
      blockStore.setConfiguration({
        ...blockStore.configuration,
        chat_history_source: enabled ? "" : undefined,
      } as PromptTemplateConfiguration);
    },
    [blockStore],
  );

  const handleChatHistorySourceChange = useCallback(
    (source: string) => {
      blockStore.setConfiguration({
        ...blockStore.configuration,
        chat_history_source: source || undefined,
      } as PromptTemplateConfiguration);
    },
    [blockStore],
  );

  const handleMockToolResponsesToggle = useCallback(
    (enabled: boolean) => {
      blockStore.setConfiguration({
        ...blockStore.configuration,
        mock_tool_responses: enabled,
        // Drop the scenario/stop-tools when turning mocking off so stale text doesn't persist.
        mock_scenario: enabled ? blockStore.configuration?.mock_scenario : undefined,
        mock_stop_tools: enabled ? blockStore.configuration?.mock_stop_tools : undefined,
      } as PromptTemplateConfiguration);
    },
    [blockStore],
  );

  const handleMockScenarioChange = useCallback(
    (scenario: string) => {
      blockStore.setConfiguration({
        ...blockStore.configuration,
        mock_scenario: scenario || undefined,
      } as PromptTemplateConfiguration);
    },
    [blockStore],
  );

  const handleMockStopToolsChange = useCallback(
    (tools: string) => {
      blockStore.setConfiguration({
        ...blockStore.configuration,
        mock_stop_tools: tools || undefined,
      } as PromptTemplateConfiguration);
    },
    [blockStore],
  );

  const handleEngineUpdate = useCallback(
    (promptData?: Partial<PromptBlueprint>) => {
      try {
        if (!promptData) {
          blockStore.setConfiguration({
            ...blockStore.configuration,
            engine: undefined,
          } as PromptTemplateConfiguration);
          return;
        }

        if (typeof promptData !== "object") {
          // eslint-disable-next-line no-console
          console.warn("Invalid prompt data for engine update:", promptData);
          return;
        }

        const isCustomProvider = Boolean(
          providerStore.providers.find(provider => provider.id === promptData.provider_id)
            ?.workspace_id,
        );

        blockStore.setConfiguration({
          ...blockStore.configuration,
          engine: {
            provider_base_url_name: promptData.provider_base_url_name || null,
            provider: isCustomProvider ? null : promptData.metadata?.model?.provider,
            model: promptData.metadata?.model?.name,
            parameters: promptData.metadata?.model?.parameters,
            model_config_id: promptData.metadata?.model?.model_config_id || null,
            provider_id: isCustomProvider ? promptData.provider_id || null : null,
            api_type: promptData.metadata?.model?.api_type || null,
          },
        } as PromptTemplateConfiguration);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error updating engine configuration:", error);
      }
    },
    [blockStore, providerStore],
  );

  const promptData = useMemo(() => {
    if (blockStore.configuration?.engine) {
      return {
        metadata: {
          model: {
            provider: blockStore.configuration.engine.provider,
            name: blockStore.configuration.engine.model,
            parameters: blockStore.configuration.engine.parameters,
            model_config_id: blockStore.configuration.engine.model_config_id,
            api_type: blockStore.configuration.engine.api_type,
          },
        },
        provider_base_url_name: blockStore.configuration.engine.provider_base_url_name,
        provider_id: blockStore.configuration.engine.provider_id
          ? blockStore.configuration.engine.provider_id
          : undefined,
      } as Partial<PromptBlueprint>;
    } else if (selectedPromptTemplateVersion?.metadata) {
      return {
        metadata: selectedPromptTemplateVersion.metadata,
        provider_base_url_name: selectedPromptTemplateVersion.provider_base_url_name,
        provider_id: selectedPromptTemplateVersion.provider_id,
      } as Partial<PromptBlueprint>;
    }
    return undefined;
  }, [blockStore.configuration?.engine, selectedPromptTemplateVersion]);

  // Add defaultPromptData that always represents the template's default configuration
  const defaultPromptData = useMemo(() => {
    if (selectedPromptTemplateVersion?.metadata) {
      return {
        metadata: selectedPromptTemplateVersion.metadata,
        provider_base_url_name: selectedPromptTemplateVersion.provider_base_url_name,
        provider_id: selectedPromptTemplateVersion.provider_id,
      } as Partial<PromptBlueprint>;
    }
    return undefined;
  }, [selectedPromptTemplateVersion]);

  const messages = useMemo(() => {
    if (selectedPromptTemplateVersion?.prompt_template) {
      const template = selectedPromptTemplateVersion.prompt_template;
      if (template && "type" in template && template.type === "chat" && template.messages) {
        return template.messages.map(msg => getStringContent(msg)).filter(Boolean);
      }
      return undefined;
    }
    return undefined;
  }, [selectedPromptTemplateVersion]);

  return {
    blockStore,
    promptTemplateVersionQuery,
    promptId,
    selectedPromptTemplateVersion,
    parseInputVariables,
    inputVariables,
    templateFormat,
    handleTemplateSelection,
    handleVerboseChange,
    handleReturnTemplateOnlyChange,
    handleHydrateInputVariablesChange,
    handleChatHistoryToggle,
    handleChatHistorySourceChange,
    handleMockToolResponsesToggle,
    handleMockScenarioChange,
    handleMockStopToolsChange,
    handleEngineUpdate,
    promptData,
    defaultPromptData,
    releaseLabelExists,
    messages,
  };
};
